// src/pools/ai/model-sub-purpose.schema.ts
import { z } from "zod";
var TEXT_LLM_SUB_PURPOSES = {
  create: [],
  edit: [],
  enhance: [],
  tools: []
};
var IMAGE_SUB_PURPOSES = {
  create: [],
  edit: [],
  enhance: ["upscale", "denoise", "restore", "super-resolution", "sharpen", "hdr"],
  tools: [
    "inpaint",
    "outpaint",
    "controlnet",
    "mask",
    "background-removal",
    "style-transfer",
    "reimagine",
    "object-removal",
    "depth-estimation",
    "pose-estimation",
    "edge-detection",
    "segmentation",
    "captioning",
    "face-swap",
    "face-retouching",
    "virtual-try-on",
    "relighting",
    "colorization",
    "vectorization",
    "product-photography",
    "training"
  ]
};
var VIDEO_SUB_PURPOSES = {
  create: ["text-to-video", "image-to-video"],
  edit: [],
  enhance: ["video-upscale"],
  tools: [
    "lipsync",
    "video-extend",
    "frame-interpolation",
    "motion-transfer",
    "frame-extraction",
    "video-captioning",
    "video-segmentation",
    "ffmpeg",
    "reframe",
    "training"
  ]
};
var SOUND_SUB_PURPOSES = {
  create: ["text-to-speech", "text-to-music", "text-to-sound"],
  edit: [],
  enhance: ["audio-enhance"],
  tools: ["transcription", "voice-conversion", "audio-analysis", "waveform"]
};
var THREE_D_SUB_PURPOSES = {
  create: ["text-to-3d", "image-to-3d"],
  edit: ["remesh", "retexture", "part-editing"],
  enhance: [],
  tools: []
};
var NODE_SUB_PURPOSE_MAP = {
  "text-llm": {
    create: TEXT_LLM_SUB_PURPOSES.create,
    edit: TEXT_LLM_SUB_PURPOSES.edit,
    enhance: TEXT_LLM_SUB_PURPOSES.enhance,
    tools: TEXT_LLM_SUB_PURPOSES.tools
  },
  image: {
    create: IMAGE_SUB_PURPOSES.create,
    edit: IMAGE_SUB_PURPOSES.edit,
    enhance: IMAGE_SUB_PURPOSES.enhance,
    tools: IMAGE_SUB_PURPOSES.tools
  },
  video: {
    create: VIDEO_SUB_PURPOSES.create,
    edit: VIDEO_SUB_PURPOSES.edit,
    enhance: VIDEO_SUB_PURPOSES.enhance,
    tools: VIDEO_SUB_PURPOSES.tools
  },
  sound: {
    create: SOUND_SUB_PURPOSES.create,
    edit: SOUND_SUB_PURPOSES.edit,
    enhance: SOUND_SUB_PURPOSES.enhance,
    tools: SOUND_SUB_PURPOSES.tools
  },
  "3d": {
    create: THREE_D_SUB_PURPOSES.create,
    edit: THREE_D_SUB_PURPOSES.edit,
    enhance: THREE_D_SUB_PURPOSES.enhance,
    tools: THREE_D_SUB_PURPOSES.tools
  }
};
var AiModelSubPurposeSchema = z.enum([
  // Image - Enhance
  "upscale",
  "denoise",
  "restore",
  "super-resolution",
  "sharpen",
  "hdr",
  // Image - Tools
  "inpaint",
  "outpaint",
  "controlnet",
  "mask",
  "background-removal",
  "style-transfer",
  "reimagine",
  "object-removal",
  "depth-estimation",
  "pose-estimation",
  "edge-detection",
  "segmentation",
  "captioning",
  "face-swap",
  "face-retouching",
  "virtual-try-on",
  "relighting",
  "colorization",
  "vectorization",
  "product-photography",
  "training",
  // Video - Create
  "text-to-video",
  "image-to-video",
  // Video - Enhance
  "video-upscale",
  // Video - Tools
  "lipsync",
  "video-extend",
  "frame-interpolation",
  "motion-transfer",
  "frame-extraction",
  "video-captioning",
  "video-segmentation",
  "ffmpeg",
  "reframe",
  // 'training' already listed under Image - Tools
  // Sound - Create
  "text-to-speech",
  "text-to-music",
  "text-to-sound",
  // Sound - Enhance
  "audio-enhance",
  // Sound - Tools
  "transcription",
  "voice-conversion",
  "audio-analysis",
  "waveform",
  // 3D - Create
  "text-to-3d",
  "image-to-3d",
  // 3D - Edit
  "remesh",
  "retexture",
  "part-editing"
]);
function getValidSubPurposes(nodeType, purpose) {
  const nodeMap = NODE_SUB_PURPOSE_MAP[nodeType];
  if (!nodeMap) return [];
  const subPurposes = nodeMap[purpose];
  return subPurposes || [];
}
function isValidSubPurpose(nodeType, purpose, subPurpose) {
  const validSubPurposes = getValidSubPurposes(nodeType, purpose);
  return validSubPurposes.includes(subPurpose);
}
function getAllSubPurposesForNodeType(nodeType) {
  const nodeMap = NODE_SUB_PURPOSE_MAP[nodeType];
  if (!nodeMap) return [];
  const allSubPurposes = /* @__PURE__ */ new Set();
  Object.values(nodeMap).forEach((subPurposes) => {
    subPurposes.forEach((sp) => allSubPurposes.add(sp));
  });
  return Array.from(allSubPurposes);
}
function getSubPurposeLabel(subPurpose) {
  return subPurpose.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
var TOOL_CATEGORIES = {
  "fill-and-extend": { label: "Fill & Extend", icon: "paint-bucket" },
  "remove-and-clean": { label: "Remove & Clean", icon: "eraser" },
  "style-and-color": { label: "Style & Color", icon: "palette" },
  "face-and-body": { label: "Face & Body", icon: "scan-face" },
  "control-and-guidance": { label: "Control & Guidance", icon: "sliders-horizontal" },
  "analyze-and-caption": { label: "Analyze & Caption", icon: "scan-text" },
  "convert": { label: "Convert", icon: "arrow-right-left" },
  "motion": { label: "Motion", icon: "clapperboard" },
  "voice": { label: "Voice", icon: "mic" },
  "commercial": { label: "Commercial", icon: "shopping-bag" },
  "training": { label: "Training", icon: "brain" }
};
var TOOL_CATEGORY_VALUES = [
  "fill-and-extend",
  "remove-and-clean",
  "style-and-color",
  "face-and-body",
  "control-and-guidance",
  "analyze-and-caption",
  "convert",
  "motion",
  "voice",
  "commercial",
  "training"
];
var ToolCategorySchema = z.enum(TOOL_CATEGORY_VALUES);
var SUB_PURPOSE_TO_TOOL_CATEGORY = {
  // Fill & Extend
  "inpaint": "fill-and-extend",
  "outpaint": "fill-and-extend",
  "video-extend": "fill-and-extend",
  // Remove & Clean
  "background-removal": "remove-and-clean",
  "object-removal": "remove-and-clean",
  // Style & Color
  "style-transfer": "style-and-color",
  "reimagine": "style-and-color",
  "colorization": "style-and-color",
  "relighting": "style-and-color",
  // Face & Body
  "face-swap": "face-and-body",
  "face-retouching": "face-and-body",
  "virtual-try-on": "face-and-body",
  "lipsync": "face-and-body",
  // Control & Guidance
  "controlnet": "control-and-guidance",
  "mask": "control-and-guidance",
  "depth-estimation": "control-and-guidance",
  "pose-estimation": "control-and-guidance",
  "edge-detection": "control-and-guidance",
  "segmentation": "control-and-guidance",
  // Analyze & Caption
  "captioning": "analyze-and-caption",
  "video-captioning": "analyze-and-caption",
  "video-segmentation": "analyze-and-caption",
  "audio-analysis": "analyze-and-caption",
  // Convert
  "vectorization": "convert",
  "frame-extraction": "convert",
  "ffmpeg": "convert",
  "waveform": "convert",
  // Motion
  "frame-interpolation": "motion",
  "motion-transfer": "motion",
  "reframe": "style-and-color",
  // Voice
  "transcription": "voice",
  "voice-conversion": "voice",
  // Commercial
  "product-photography": "commercial",
  // Training
  "training": "training"
};
function getToolCategory(subPurpose) {
  return SUB_PURPOSE_TO_TOOL_CATEGORY[subPurpose] ?? null;
}
function getToolCategoriesForNodeType(nodeType) {
  const validSubPurposes = getValidSubPurposes(nodeType, "tools");
  const grouped = {};
  for (const sp of validSubPurposes) {
    const cat = SUB_PURPOSE_TO_TOOL_CATEGORY[sp];
    if (cat) {
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(sp);
    }
  }
  return TOOL_CATEGORY_VALUES.filter((cat) => grouped[cat]?.length).map((cat) => ({
    category: cat,
    label: TOOL_CATEGORIES[cat].label,
    icon: TOOL_CATEGORIES[cat].icon,
    subPurposes: grouped[cat]
  }));
}

export {
  TEXT_LLM_SUB_PURPOSES,
  IMAGE_SUB_PURPOSES,
  VIDEO_SUB_PURPOSES,
  SOUND_SUB_PURPOSES,
  THREE_D_SUB_PURPOSES,
  NODE_SUB_PURPOSE_MAP,
  AiModelSubPurposeSchema,
  getValidSubPurposes,
  isValidSubPurpose,
  getAllSubPurposesForNodeType,
  getSubPurposeLabel,
  TOOL_CATEGORIES,
  TOOL_CATEGORY_VALUES,
  ToolCategorySchema,
  SUB_PURPOSE_TO_TOOL_CATEGORY,
  getToolCategory,
  getToolCategoriesForNodeType
};
//# sourceMappingURL=chunk-NUTMEM5W.js.map